import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage, BarModelConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { Sparkles, HelpCircle, Delete } from 'lucide-react-native';

interface BarModelActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const BarModelActivity: React.FC<BarModelActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as BarModelConfig;
    const [inputValue, setInputValue] = useState<string>('');
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [feedbackMessage, setFeedbackMessage] = useState<string>(config.hint || 'Study the bar model and find the missing value.');
    const [hasCompleted, setHasCompleted] = useState(false);

    const totalUnits = useMemo(() => {
        return (config.bars || []).reduce((acc, bar) => acc + (bar.units || 1), 0) || 1;
    }, [config.bars]);

    const handleDigitPress = (digit: string) => {
        if (hasCompleted) return;
        if (inputValue.length < 5) {
            setInputValue((prev) => prev + digit);
            setTrafficStatus('idle');
        }
    };

    const handleBackspace = () => {
        if (hasCompleted) return;
        setInputValue((prev) => prev.slice(0, -1));
        setTrafficStatus('idle');
    };

    const handleCheck = () => {
        if (!inputValue || hasCompleted) return;
        const num = parseFloat(inputValue);
        const expected = config.expectedAnswer;

        if (num === expected) {
            setTrafficStatus('correct');
            setFeedbackMessage('Spot on! You correctly solved the bar model problem.');
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: num,
                mechanic: 'bar_model',
                isCorrect: true,
                score: 100,
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else {
            setTrafficStatus('incorrect');
            setFeedbackMessage('Not quite right. Look at the total and check how many equal parts there are.');
        }
    };

    return (
        <StageCard testID="bar-model-card">
            <View style={styles.container}>
                {/* Header Row */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <Sparkles size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>Bar Model Problem Solver</Text>
                        </View>
                        <Text style={styles.wordProblem}>{config.wordProblem || config.question}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        {config.wordProblem && (
                            <AudioPromptButton
                                promptText={config.wordProblem}
                                activityId={activity.id}
                                testID="bar-model-audio-button"
                            />
                        )}
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Bar Model Visual Diagram */}
                <View style={styles.diagramContainer}>
                    {/* Total Bracket Header */}
                    {config.totalValue !== undefined && (
                        <View style={styles.totalBracketContainer}>
                            <View style={styles.bracketLine} />
                            <View style={styles.totalPill}>
                                <Text style={styles.totalPillText}>
                                    Total: {config.totalValue} {config.unitLabel || ''}
                                </Text>
                            </View>
                        </View>
                    )}

                    {/* Proportional Bars */}
                    <View style={styles.barsStack}>
                        {(config.bars || []).map((bar) => {
                            const pct = Math.max(15, Math.round((bar.units / totalUnits) * 100));
                            return (
                                <View key={bar.id} style={styles.barRow}>
                                    <View style={styles.barLabelCol}>
                                        <Text style={styles.barLabelText}>{bar.label}</Text>
                                    </View>
                                    <View style={styles.barTrack}>
                                        <View
                                            style={[
                                                styles.barSegment,
                                                { width: `${pct}%`, backgroundColor: bar.color || '#3B82F6' },
                                                bar.isUnknown && styles.unknownBarSegment,
                                            ]}
                                        >
                                            {bar.isUnknown ? (
                                                <View style={styles.unknownBadge}>
                                                    <HelpCircle size={16} color="#B45309" />
                                                    <Text style={styles.unknownText}>?</Text>
                                                </View>
                                            ) : (
                                                <Text style={styles.barSegmentText}>
                                                    {bar.value !== undefined ? bar.value : `${bar.units} units`}
                                                </Text>
                                            )}
                                        </View>
                                    </View>
                                </View>
                            );
                        })}
                    </View>
                </View>

                {/* Question & Interactive Numeric Numpad */}
                <View style={styles.inputSection}>
                    <Text style={styles.questionPrompt}>{config.question || 'What is the missing value?'}</Text>
                    
                    <View style={styles.answerDisplayRow}>
                        <Text style={styles.answerLabel}>Answer:</Text>
                        <View style={styles.displayBox}>
                            <Text style={styles.displayText}>{inputValue || '?'}</Text>
                        </View>
                        {config.unitLabel && (
                            <Text style={styles.unitSuffix}>{config.unitLabel}</Text>
                        )}
                    </View>

                    <Text style={styles.feedbackText}>{feedbackMessage}</Text>

                    {/* Compact Toybox Numpad */}
                    <View style={styles.numpadGrid}>
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map((digit) => (
                            <TouchableOpacity
                                key={digit}
                                style={styles.numpadKey}
                                onPress={() => handleDigitPress(digit)}
                                disabled={hasCompleted}
                                accessibilityLabel={`Digit ${digit}`}
                                testID={`numpad-${digit}`}
                            >
                                <Text style={styles.numpadKeyText}>{digit}</Text>
                            </TouchableOpacity>
                        ))}
                        <TouchableOpacity
                            style={[styles.numpadKey, styles.backspaceKey]}
                            onPress={handleBackspace}
                            disabled={hasCompleted}
                            accessibilityLabel="Backspace"
                            testID="numpad-backspace"
                        >
                            <Delete size={20} color="#475569" />
                        </TouchableOpacity>
                        <JuicyButton
                            label="CHECK"
                            variant="primary"
                            size="medium"
                            onPress={handleCheck}
                            disabled={hasCompleted || !inputValue}
                            testID="bar-model-check-button"
                        />
                    </View>
                </View>
            </View>
        </StageCard>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    wordProblem: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
        lineHeight: 22,
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    diagramContainer: {
        backgroundColor: '#F8FAFC',
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 12,
    },
    totalBracketContainer: {
        alignItems: 'center',
        gap: 4,
    },
    bracketLine: {
        width: '90%',
        height: 6,
        borderTopWidth: 2,
        borderLeftWidth: 2,
        borderRightWidth: 2,
        borderColor: '#64748B',
    },
    totalPill: {
        backgroundColor: '#1E293B',
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 12,
    },
    totalPillText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    barsStack: {
        gap: 10,
    },
    barRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    barLabelCol: {
        width: 80,
    },
    barLabelText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#475569',
    },
    barTrack: {
        flex: 1,
        height: 44,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        overflow: 'hidden',
    },
    barSegment: {
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 6,
    },
    unknownBarSegment: {
        backgroundColor: '#FEF3C7',
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: '#F59E0B',
    },
    unknownBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    unknownText: {
        fontSize: 16,
        fontWeight: '900',
        color: '#B45309',
    },
    barSegmentText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    inputSection: {
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
    },
    questionPrompt: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F172A',
    },
    answerDisplayRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    answerLabel: {
        fontSize: 16,
        fontWeight: '700',
        color: '#475569',
    },
    displayBox: {
        minWidth: 80,
        height: 48,
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        borderColor: '#3B82F6',
        borderRadius: 12,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 12,
    },
    displayText: {
        fontSize: 22,
        fontWeight: '900',
        color: '#1E293B',
    },
    unitSuffix: {
        fontSize: 15,
        fontWeight: '700',
        color: '#64748B',
    },
    feedbackText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#64748B',
        textAlign: 'center',
    },
    numpadGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: 420,
        gap: 8,
        marginTop: 4,
    },
    numpadKey: {
        width: 54,
        height: 48,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        borderBottomWidth: 3,
        borderBottomColor: '#94A3B8',
        justifyContent: 'center',
        alignItems: 'center',
    },
    backspaceKey: {
        backgroundColor: '#F1F5F9',
    },
    numpadKeyText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1E293B',
    },
});
